import React, { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";

/**
 * Small floating "Get a Quote" CTA pill.
 *
 * Behaviour:
 *  • Hidden while the user is at the top of the page (inline hero quote
 *    form is already in view — no need to nag).
 *  • Fades in once they scroll past ~80% of the viewport.
 *  • Tucks itself away when the user reaches the in-page quote section
 *    (#quote-form) or the contact section (#contact) so it never
 *    duplicates a visible CTA.
 *  • Sits bottom-right with a soft glass shadow — purely additive,
 *    `pointer-events: auto` only on the pill itself so it never blocks
 *    surrounding content selection.
 */
const StickyQuoteButton = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        const y = window.scrollY || window.pageYOffset || 0;
        const vh = window.innerHeight;
        // Show after the hero is mostly scrolled past
        const passedHero = y > vh * 0.7;

        // Hide if the inline quote form OR contact section is on-screen
        let nearForm = false;
        ["quote-form", "contact"].forEach((id) => {
          const el = document.getElementById(id);
          if (!el) return;
          const rect = el.getBoundingClientRect();
          if (rect.top < vh && rect.bottom > 0) nearForm = true;
        });

        setShow(passedHero && !nearForm);
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div
      aria-hidden={!show}
      data-testid="sticky-quote-wrap"
      className="fixed bottom-20 right-5 sm:bottom-20 sm:right-6 z-40 pointer-events-none"
    >
      <a
        href="#quote-form"
        data-testid="sticky-quote-button"
        className={`pointer-events-auto group inline-flex items-center gap-2 h-12 sm:h-13 pl-5 pr-4 rounded-full bg-[#daae9c] text-ink border border-[#daae9c] shadow-[0_12px_30px_-10px_rgba(138,93,72,0.55)] hover:bg-white hover:text-ink hover:border-[#daae9c] transition-all duration-300 ${
          show
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-3 pointer-events-none"
        }`}
        style={{ backdropFilter: "saturate(140%)" }}
      >
        <span className="text-sm font-medium tracking-wide">
          Request a Quote
        </span>
        <span className="w-7 h-7 rounded-full bg-white/40 group-hover:bg-[#daae9c]/20 flex items-center justify-center transition-colors">
          <ArrowRight
            size={14}
            className="group-hover:translate-x-0.5 transition-transform"
          />
        </span>
      </a>
    </div>
  );
};

export default StickyQuoteButton;
